<template>
  <v-card :loading="loading" v-bind="$attrs" v-on="$listeners">
    <v-card-title>
      <v-row>
        <v-col>
          <span class="headline">My Dealers</span>
        </v-col>
        <v-col class="text-right">
          <v-btn
            v-if="!isWidget"
            small
            outlined
            color="primary"
            class="mr-2"
            :disabled="loading || !items.length"
            @click="exportDealers"
          >
            <v-icon left small>mdi-download</v-icon>
            Export
          </v-btn>
          <v-btn v-if="isWidget" small color="primary" :to="{ name: 'myDealers' }">View All</v-btn>
        </v-col>
      </v-row>
    </v-card-title>
    <v-card-subtitle>
      Summary of dealers reporting to you.
    </v-card-subtitle>

    <v-card-text>
      <v-data-table
        :headers="headers"
        :items="items"
        :loading="loading"
        dense
        :options.sync="options"
        :server-items-length="total"
        :hide-default-footer="isWidget"
        no-data-text="No dealers found reporting to you."
      >
        <template slot="item.name" slot-scope="{ item }">
          <a @click.prevent="viewDealerParticipants(item.id)" href="#">
            {{ item.name }}
          </a>
        </template>
      </v-data-table>
    </v-card-text>
  </v-card>
</template>

<script>
import { mapGetters } from "vuex";

export default {
  name: "MyDealers",
  props: {
    isWidget: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      loading: false,
      items: [],
      total: 0,
      options: {
        itemsPerPage: this.isWidget ? 5 : 10,
        page: 1,
        sortBy: ["name"],
        sortDesc: [false]
      },
      headers: [
        { text: "Dealer Number", value: "organizationKey" },
        { text: "Dealer Name", value: "name" },
        {
          text: "Enrolled Participants",
          value: "enrolledParticipantsCount",
          sortable: false,
          align: "right"
        }
      ]
    };
  },
  computed: {
    ...mapGetters(["selectedParticipant"])
  },
  metaInfo() {
    if (this.isWidget) return {};
    return {
      title: "My Dealers"
    };
  },
  mounted() {
    this.fetchData();
  },
  watch: {
    options: {
      handler() {
        this.fetchData();
      },
      deep: true
    },
    selectedParticipant() {
      this.fetchData();
    }
  },
  methods: {
    async fetchData() {
      const organizationId = this.selectedParticipant?.organization?.id;
      if (!organizationId) {
        this.items = [];
        this.total = 0;
        this.loading = false;
        console.info("No district ID found for the selected participant.");

        return;
      }

      this.loading = true;
      const searchForm = {
        parentOrganization: { id: organizationId },
        isEffective: "Active"
      };
      const params = this.$util.buildPaginationParams(this.options);
      try {
        const { data } = await this.$api.post(`/api/organizations/search?${params.toString()}`, searchForm);
        const dealers = data.content || [];

        await this.addParticipantCounts(dealers);

        this.items = dealers;
        this.total = data.totalElements || 0;
      } catch (error) {
        const errorMessage = this.$api.getErrorsFromResponse(error);
        console.error("Error fetching dealers:", errorMessage);
        this.items = [];
        this.total = 0;
      } finally {
        this.loading = false;
      }
    },
    viewDealerParticipants(dealerId) {
      this.$router.push({ name: "dealerParticipants", query: { dealerId } });
    },
    exportDealers() {
      this.loading = true;
      const searchForm = {
        parentOrganization: { id: this.selectedParticipant?.organization?.id },
        isEffective: "Active"
      };

      this.$util
        .fetchPaginatedData("/api/organizations/search", searchForm, 100)
        .then(dealers =>
          this.addParticipantCounts(dealers).then(() => this.$export.exportToCsv("my_dealers", this.headers, dealers))
        )
        .finally(() => {
          this.loading = false;
        });
    },
    addParticipantCounts(dealers) {
      return Promise.all(
        dealers.map(async dealer => {
          const { data } = await this.$api.post("/api/participants/search?size=1", {
            organization: { id: dealer.id },
            isEffective: "Active"
          });
          dealer.enrolledParticipantsCount = data.totalElements;
        })
      );
    }
  }
};
</script>
